import React, { useState } from 'react';
import { 
  Users, Sparkles, CheckCircle, AlertTriangle, PlayCircle, Download,
  Database, Shield, FileText, Calendar, TestTube, BarChart3,
  GitMerge, Settings, UserCheck, Layers
} from 'lucide-react';

const BusinessPartnerMigration = ({ project }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/bpce/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: project?.id || 'demo-project',
          use_mock_data: true,
          include_ai_narratives: true
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      setAnalysis(data);
      setActiveTab('overview');
    } catch (error) {
      console.error('Error running BP conversion analysis:', error);
      alert('Failed to run analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!analysis) return;
    
    const report = JSON.stringify(analysis, null, 2);
    const blob = new Blob([report], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bp-conversion-${analysis.analysis_id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'current-model', label: 'Current Model', icon: Database },
    { id: 'target-design', label: 'Target BP Design', icon: Settings },
    { id: 'cvi-blueprint', label: 'CVI Blueprint', icon: FileText },
    { id: 'data-quality', label: 'Data Quality', icon: Shield },
    { id: 'harmonization', label: 'Harmonization', icon: GitMerge },
    { id: 'backlog', label: 'Conversion Backlog', icon: CheckCircle },
    { id: 'waves', label: 'Wave Planning', icon: Calendar },
    { id: 'testing', label: 'Testing', icon: TestTube },
    { id: 'ai-insights', label: 'AI Insights', icon: Sparkles }
  ];

  if (!analysis) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-3xl font-bold mb-2">Business Partner Conversion Engine</h2>
          <p className="text-gray-600 mb-6">
            Comprehensive customer/vendor to Business Partner migration analysis with CVI planning and AI-powered insights
          </p>
          <button
            onClick={runAnalysis}
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
          >
            <PlayCircle className="w-5 h-5" />
            <span>{loading ? 'Running Analysis...' : 'Run BP Conversion Analysis'}</span>
          </button>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">What This Module Analyzes:</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <Users className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Partner Model Assessment</h4>
                <p className="text-sm text-gray-600">Customer (KNA1) and vendor (LFA1) master data analysis</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Settings className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Target BP Design</h4>
                <p className="text-sm text-gray-600">BP roles (FLCU/FLVN), groupings, and number range strategy</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">CVI Conversion Blueprint</h4>
                <p className="text-sm text-gray-600">Customer Vendor Integration planning and sequencing</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Shield className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Data Quality & Harmonization</h4>
                <p className="text-sm text-gray-600">Duplicate detection, data cleansing, merge recommendations</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">5-Wave Conversion Plan</h4>
                <p className="text-sm text-gray-600">Assessment → Trial → Cleanup → Conversion → Validation</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <TestTube className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">E2E Testing</h4>
                <p className="text-sm text-gray-600">OTC, P2P, credit management, dunning test scenarios</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-2">Business Partner Conversion Engine</h2>
            <p className="text-gray-600">Analysis ID: {analysis.analysis_id}</p>
            <p className="text-sm text-gray-500">Generated: {new Date(analysis.generated_at).toLocaleString()}</p>
            {analysis.ai_narratives?.generated_by === 'GPT-5.1' && (
              <div className="mt-2 flex items-center space-x-2 text-sm text-purple-600">
                <Sparkles className="w-4 h-4" />
                <span>AI-powered insights by GPT-5.1</span>
              </div>
            )}
          </div>
          <button
            onClick={downloadReport}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <Users className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_partners.toLocaleString()}</div>
          <div className="text-sm opacity-90">Total Partners</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <UserCheck className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.estimated_bps_after_conversion.toLocaleString()}</div>
          <div className="text-sm opacity-90">Estimated BPs</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <GitMerge className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.duplicates_to_merge}</div>
          <div className="text-sm opacity-90">Duplicates to Merge</div>
        </div>
        <div className={`bg-gradient-to-br rounded-xl p-6 text-white ${
          analysis.summary.data_quality_score >= 80 ? 'from-green-500 to-green-600' :
          analysis.summary.data_quality_score >= 60 ? 'from-yellow-500 to-yellow-600' :
          'from-red-500 to-red-600'
        }`}>
          <Shield className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.data_quality_score}%</div>
          <div className="text-sm opacity-90">Data Quality Score</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md">
        <div className="border-b overflow-x-auto">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600 font-semibold'
                      : 'border-transparent text-gray-600 hover:text-purple-600 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{analysis.summary.total_customers.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Customers</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{analysis.summary.total_vendors.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Vendors</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{analysis.summary.total_data_quality_issues.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Data Quality Issues</div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Conversion Summary</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.cleanup_effort_days}</div>
                    <div className="text-sm text-gray-600">Cleanup Days</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.total_conversion_weeks}</div>
                    <div className="text-sm text-gray-600">Conversion Weeks</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.total_test_scenarios}</div>
                    <div className="text-sm text-gray-600">Test Scenarios</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.total_backlog_items}</div>
                    <div className="text-sm text-gray-600">Backlog Items</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Model Tab */}
          {activeTab === 'current-model' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-4">Customer Data</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(analysis.current_partner_model.customers).filter(([key]) => !['customer_number_ranges', 'partner_functions'].includes(key)).map(([key, value]) => (
                    <div key={key} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                      <div className="text-xl font-bold text-gray-800">
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                         Array.isArray(value) ? value.join(', ') :
                         typeof value === 'number' ? value.toLocaleString() :
                         value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Vendor Data</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(analysis.current_partner_model.vendors).filter(([key]) => !['vendor_number_ranges', 'partner_functions', 'payment_methods'].includes(key)).map(([key, value]) => (
                    <div key={key} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                      <div className="text-xl font-bold text-gray-800">
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                         Array.isArray(value) ? value.join(', ') :
                         typeof value === 'number' ? value.toLocaleString() :
                         value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Identified Issues</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(analysis.current_partner_model.issues).map(([key, value]) => (
                    <div key={key} className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                      <div className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                      <div className="text-2xl font-bold text-red-600">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Target Design Tab */}
          {activeTab === 'target-design' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-bold mb-2">Target BP Model</h4>
                <p className="text-sm text-gray-600">Estimated BPs after conversion: <span className="font-bold text-blue-600">{analysis.bp_target_design.estimated_bp_count.toLocaleString()}</span></p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">BP Groupings</h3>
                <div className="space-y-3">
                  {analysis.bp_target_design.bp_groupings.map((grouping, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <h4 className="font-bold mb-2">{grouping.name}</h4>
                      <div className="grid md:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-600">Number Range:</span> <span className="font-semibold">{grouping.number_range}</span></div>
                        <div><span className="text-gray-600">Internal:</span> <span className="font-semibold">{grouping.internal ? 'Yes' : 'No'}</span></div>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-600 mb-1">BP Roles:</p>
                        <div className="flex flex-wrap gap-1">
                          {grouping.bp_roles.map((role, i) => (
                            <span key={i} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{role}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Number Range Strategy</h3>
                <div className="p-4 border-2 border-gray-200 rounded-lg">
                  <div className="mb-4">
                    <span className="text-sm text-gray-600">Strategy Type: </span>
                    <span className="font-bold">{analysis.bp_target_design.number_range_strategy.strategy_type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="space-y-2">
                    {analysis.bp_target_design.number_range_strategy.proposed_ranges.map((range, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="font-semibold">{range.range_id}</span>
                        <span className="text-sm text-gray-600">{range.from} - {range.to}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{range.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CVI Blueprint Tab */}
          {activeTab === 'cvi-blueprint' && (
            <div className="space-y-6">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-bold mb-2">CVI Conversion Blueprint</h4>
                <p className="text-sm text-gray-600">Total Duration: <span className="font-bold text-purple-600">{analysis.cvi_conversion_blueprint.total_duration_days} days</span></p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Prechecks</h3>
                <div className="space-y-2">
                  {analysis.cvi_conversion_blueprint.prechecks.map((check, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold">{check.title}</h4>
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">{check.estimated_hours}h</span>
                      </div>
                      <p className="text-sm text-gray-600">{check.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Conversion Steps</h3>
                <div className="space-y-3">
                  {analysis.cvi_conversion_blueprint.conversion_steps.map((step, index) => (
                    <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                      <div className={`p-4 bg-gradient-to-r ${
                        index % 3 === 0 ? 'from-purple-500 to-purple-600' :
                        index % 3 === 1 ? 'from-blue-500 to-blue-600' :
                        'from-indigo-500 to-indigo-600'
                      } text-white`}>
                        <h4 className="font-bold text-lg">Step {step.step}: {step.phase}</h4>
                        <p className="text-sm opacity-90 mt-1">Duration: {step.duration_days} days</p>
                      </div>
                      <div className="p-4 bg-gray-50">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Activities:</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {step.activities.map((activity, i) => (
                            <li key={i}>• {activity}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Data Quality Tab */}
          {activeTab === 'data-quality' && (
            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
                <h4 className="font-bold mb-2">Overall Data Quality</h4>
                <div className="flex items-center space-x-4">
                  <div className="text-4xl font-bold text-orange-600">{analysis.data_quality_issues.overall_quality_score}%</div>
                  <div>
                    <p className="font-semibold">{analysis.data_quality_issues.quality_level}</p>
                    <p className="text-sm text-gray-600">{analysis.data_quality_issues.total_issues.toLocaleString()} issues found</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Issues by Category</h3>
                <div className="space-y-3">
                  {Object.entries(analysis.data_quality_issues.issues_by_category).map(([category, data]) => (
                    <div key={category} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold">{category}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          data.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          data.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {data.severity}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Total Issues: <span className="font-bold">{data.count.toLocaleString()}</span></p>
                      {data.examples && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold text-gray-700 mb-1">Examples:</p>
                          <div className="space-y-1">
                            {data.examples.map((example, i) => (
                              <div key={i} className="text-xs text-gray-600 flex justify-between">
                                <span>{example.field || example.type || example.status}</span>
                                <span className="font-semibold">{(example.missing_count || example.count).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Field Quality Scores</h3>
                <div className="space-y-2">
                  {analysis.data_quality_issues.field_quality.map((field, index) => (
                    <div key={index} className="p-3 border-2 border-gray-200 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">{field.field}</span>
                        <span className={`text-sm font-bold ${
                          field.quality_score >= 90 ? 'text-green-600' :
                          field.quality_score >= 75 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {field.quality_score}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            field.quality_score >= 90 ? 'bg-green-500' :
                            field.quality_score >= 75 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${field.quality_score}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Harmonization Tab */}
          {activeTab === 'harmonization' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-bold mb-2">Harmonization Plan</h4>
                <p className="text-sm text-gray-600">Total Effort: <span className="font-bold text-blue-600">{analysis.harmonization_plan.total_effort_days} days</span></p>
                <p className="text-sm text-gray-600">Expected Quality Improvement: <span className="font-bold text-green-600">{analysis.harmonization_plan.expected_quality_improvement}</span></p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Merge Candidates (Top 10)</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vendor ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Legal Entity</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Confidence</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {analysis.harmonization_plan.merge_candidates.map((candidate, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{candidate.customer_id}</td>
                          <td className="px-4 py-3 text-sm">{candidate.vendor_id}</td>
                          <td className="px-4 py-3 text-sm">{candidate.legal_entity_name}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${
                              candidate.match_confidence >= 95 ? 'bg-green-100 text-green-800' :
                              candidate.match_confidence >= 85 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {candidate.match_confidence}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${
                              candidate.priority === 'high' ? 'bg-red-100 text-red-800' :
                              candidate.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {candidate.priority}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Cleanup Activities</h3>
                <div className="space-y-2">
                  {analysis.harmonization_plan.cleanup_activities.map((activity, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold">{activity.title}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          activity.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          activity.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {activity.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{activity.description}</p>
                      <div className="flex space-x-4 text-sm">
                        <span className="text-gray-600">Records: <span className="font-bold">{activity.records_affected.toLocaleString()}</span></span>
                        <span className="text-gray-600">Effort: <span className="font-bold">{activity.effort_days} days</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Backlog Tab */}
          {activeTab === 'backlog' && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-bold mb-2">Conversion Backlog</h4>
                <p className="text-sm text-gray-600">{analysis.conversion_backlog.length} work items</p>
              </div>

              <div className="space-y-2">
                {analysis.conversion_backlog.map((item, index) => (
                  <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-400">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <span className="font-semibold">{item.title}</span>
                        <p className="text-xs text-gray-500 mt-1">{item.backlog_id}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        item.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {item.priority}
                      </span>
                    </div>
                    <div className="flex space-x-4 text-sm text-gray-600">
                      <span>Type: {item.type}</span>
                      <span>Effort: {item.effort_hours}h</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waves Tab */}
          {activeTab === 'waves' && (
            <div className="space-y-4">
              <div className="p-4 bg-indigo-50 rounded-lg mb-4">
                <h4 className="font-bold mb-2">5-Wave BP Conversion Plan</h4>
                <p className="text-sm text-gray-600">
                  Total duration: {analysis.summary.total_conversion_weeks} weeks
                </p>
              </div>

              {analysis.conversion_waves.map((wave, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className={`p-4 bg-gradient-to-r ${
                    index % 5 === 0 ? 'from-purple-500 to-purple-600' :
                    index % 5 === 1 ? 'from-blue-500 to-blue-600' :
                    index % 5 === 2 ? 'from-green-500 to-green-600' :
                    index % 5 === 3 ? 'from-orange-500 to-orange-600' :
                    'from-pink-500 to-pink-600'
                  } text-white`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-lg">Wave {wave.wave}: {wave.name}</h4>
                        <p className="text-sm opacity-90 mt-1">
                          Duration: {wave.duration_weeks} weeks · {wave.backlog_items} items · {wave.total_effort_hours}h
                        </p>
                      </div>
                      <Calendar className="w-8 h-8 opacity-80" />
                    </div>
                  </div>
                  {wave.tasks.length > 0 && (
                    <div className="p-4 bg-gray-50">
                      <h5 className="font-semibold mb-2 text-sm">Tasks in this wave:</h5>
                      <div className="space-y-1">
                        {wave.tasks.map((task, taskIndex) => (
                          <div key={taskIndex} className="text-sm text-gray-600">
                            • {task.title} ({task.effort_hours}h)
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Testing Tab */}
          {activeTab === 'testing' && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg mb-4">
                <h4 className="font-bold mb-2">BP Test Scenarios</h4>
                <p className="text-sm text-gray-600">{analysis.test_scenarios.length} test scenarios covering OTC, P2P, Credit, Dunning</p>
              </div>

              <div className="space-y-2">
                {analysis.test_scenarios.map((scenario, index) => (
                  <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-400">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <span className="font-semibold">{scenario.title}</span>
                        <p className="text-xs text-gray-500 mt-1">{scenario.scenario_id}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        scenario.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        scenario.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {scenario.priority}
                      </span>
                    </div>
                    <div className="flex space-x-4 text-sm text-gray-600 mb-2">
                      <span>Process: {scenario.process}</span>
                      <span>Type: {scenario.test_type}</span>
                      <span>{scenario.test_cases} test cases</span>
                      <span>{scenario.estimated_hours} hours</span>
                    </div>
                    {scenario.steps && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Steps:</p>
                        <div className="space-y-1">
                          {scenario.steps.map((step, i) => (
                            <div key={i} className="text-xs text-gray-600">• {step}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'ai-insights' && analysis.ai_narratives && (
            <div className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg flex items-center space-x-3">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <div>
                  <h4 className="font-bold">AI-Generated BP Insights</h4>
                  <p className="text-sm text-gray-600">
                    Powered by {analysis.ai_narratives.generated_by} · Generated {new Date(analysis.ai_narratives.generated_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <Users className="w-6 h-6 text-blue-600" />
                  <span>BP Model Summary</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.bp_model_summary}
                </div>
              </div>

              <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <GitMerge className="w-6 h-6 text-purple-600" />
                  <span>Duplicate Handling Strategy</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.duplicate_strategy}
                </div>
              </div>

              <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                  <span>Change Impact & Communication</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.change_impact}
                </div>
              </div>

              {analysis.ai_narratives.context_used && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-semibold mb-2 text-sm">Context Used for AI Generation:</h5>
                  <div className="grid md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Customers</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.total_customers.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Vendors</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.total_vendors.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Estimated BPs</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.estimated_bps.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Quality Score</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.data_quality_score}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Duplicates</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.duplicates}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessPartnerMigration;